import { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { api } from '../api';

const CATEGORIAS = [
  { key: 'filtros',  label: '🔧 Filtros',       color: '#4cc9f0' },
  { key: 'galones',  label: '🛢️ Galones',        color: '#f72585' },
  { key: 'litros',   label: '🧴 Litros',          color: '#7209b7' },
  { key: 'canecas',  label: '🪣 Canecas',         color: '#f4a261' },
  { key: 'varios',   label: '📦 Varios',          color: '#4361ee' },
];

function TablaTop({ datos, color, esCompra }) {
  if (!datos || datos.productos.length === 0) return (
    <p style={{ color: '#555', textAlign: 'center', padding: 24 }}>Sin datos para este período</p>
  );

  const maxVal = datos.productos[0]?.[esCompra ? 'total_comprado' : 'total_vendido'] || 1;
  const campoQty = esCompra ? 'total_comprado' : 'total_vendido';
  const campoCosto = esCompra ? 'costo_total' : 'revenue';
  const labelQty = esCompra ? 'Comprado' : 'Vendido';
  const labelCosto = esCompra ? 'Costo total' : 'Revenue';

  return (
    <div>
      <div style={s.marcaLider}>
        🏆 Marca líder: <strong style={{ color }}>{datos.marca_lider}</strong>
        <span style={s.marcaTotal}>
          · {datos.productos.reduce((a, p) => a + p[campoQty], 0).toFixed(0)} unidades
          · ${datos.productos.reduce((a, p) => a + p[campoCosto], 0).toFixed(2)} {labelCosto.toLowerCase()}
        </span>
      </div>

      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={datos.productos} layout="vertical" margin={{ left: 0, right: 20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#2a2a3e" horizontal={false} />
          <XAxis type="number" stroke="#aaa" fontSize={11} />
          <YAxis type="category" dataKey="marca" stroke="#aaa" fontSize={10} width={70} />
          <Tooltip
            contentStyle={{ background: '#1e1e2e', border: 'none', color: '#fff', fontSize: 12 }}
            formatter={(val) => [`${val} uds`, labelQty]}
          />
          <Bar dataKey={campoQty} fill={color} radius={[0, 4, 4, 0]} />
        </BarChart>
      </ResponsiveContainer>

      <div style={s.tabla}>
        <div style={s.tablaHeader}>
          <span style={{ flex: 3 }}>PRODUCTO</span>
          <span style={{ width: 80, textAlign: 'right' }}>{labelQty.toUpperCase()}</span>
          <span style={{ width: 80, textAlign: 'right' }}>STOCK</span>
          <span style={{ width: 100, textAlign: 'right' }}>{labelCosto.toUpperCase()}</span>
        </div>
        {datos.productos.map((p, i) => (
          <div key={i} style={s.tablaRow}>
            <div style={{ flex: 3 }}>
              <div style={s.itemNombre}>{p.item}</div>
              <div style={s.itemCodigo}>{p.codigo} · {p.marca}</div>
            </div>
            <div style={{ width: 80, textAlign: 'right' }}>
              <div style={s.barraContainer}>
                <div style={{ ...s.barraFill, width: `${(p[campoQty] / maxVal) * 100}%`, background: color }} />
              </div>
              <span style={{ color: '#fff', fontSize: 13, fontWeight: 'bold' }}>{p[campoQty].toFixed(0)}</span>
            </div>
            <span style={{ width: 80, textAlign: 'right', color: p.stock_actual < 5 ? '#f44336' : '#aaa', fontSize: 13 }}>
              {p.stock_actual.toFixed(0)}{p.stock_actual < 5 && ' ⚠️'}
            </span>
            <span style={{ width: 100, textAlign: 'right', color: '#4cc9f0', fontSize: 13 }}>${p[campoCosto].toFixed(2)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Productos() {
  const hoy = new Date();
  const hace30 = new Date(hoy); hace30.setDate(hoy.getDate() - 30);
  const fmt = (d) => d.toISOString().split('T')[0];

  const [fechaInicio, setFechaInicio]     = useState(fmt(hace30));
  const [fechaFin, setFechaFin]           = useState(fmt(hoy));
  const [datosVentas, setDatosVentas]     = useState(null);
  const [datosCompras, setDatosCompras]   = useState(null);
  const [cargando, setCargando]           = useState(true);
  const [categoriaActiva, setCategoriaActiva] = useState('filtros');
  const [tabActivo, setTabActivo]         = useState('ventas'); // 'ventas' | 'compras'

  useEffect(() => { cargar(); }, []);

  const cargar = async (fi = fechaInicio, ff = fechaFin) => {
    setCargando(true);
    try {
      const [ventas, compras] = await Promise.all([
        api.get(`/productos/top?fecha_inicio=${fi}&fecha_fin=${ff}`),
        api.get(`/productos/compras?fecha_inicio=${fi}&fecha_fin=${ff}`)
      ]);
      setDatosVentas(ventas.error ? null : ventas);
      setDatosCompras(compras.error ? null : compras);
    } catch (e) {
      setDatosVentas(null);
      setDatosCompras(null);
    } finally {
      setCargando(false);
    }
  };

  const aplicarFiltro = () => cargar(fechaInicio, fechaFin);

  const ultimos30 = () => {
    const fi = fmt(hace30);
    const ff = fmt(hoy);
    setFechaInicio(fi);
    setFechaFin(ff);
    cargar(fi, ff);
  };

  const datos = tabActivo === 'ventas' ? datosVentas : datosCompras;
  const categoriaActual = CATEGORIAS.find(c => c.key === categoriaActiva);

  return (
    <div>
      <div style={s.headerRow}>
        <h2 style={s.titulo}>📦 Productos</h2>
        <div style={s.filtrosRow}>
          <button style={s.botonRapido} onClick={ultimos30}>Últimos 30 días</button>
          <input type="date" style={s.inputFecha} value={fechaInicio} onChange={e => setFechaInicio(e.target.value)} />
          <span style={{ color: '#aaa' }}>→</span>
          <input type="date" style={s.inputFecha} value={fechaFin} onChange={e => setFechaFin(e.target.value)} />
          <button style={s.botonAplicar} onClick={aplicarFiltro}>Aplicar</button>
        </div>
      </div>

      {datos && (
        <p style={s.periodoTxt}>📅 {datos.fecha_inicio} — {datos.fecha_fin}</p>
      )}

      {/* Tab Ventas / Compras */}
      <div style={s.tabsMain}>
        <button
          style={{ ...s.tabMain, ...(tabActivo === 'ventas' ? s.tabMainActivo : {}) }}
          onClick={() => setTabActivo('ventas')}
        >
          📈 Ventas
        </button>
        <button
          style={{ ...s.tabMain, ...(tabActivo === 'compras' ? { ...s.tabMainActivo, borderBottomColor: '#f4a261' } : {}) }}
          onClick={() => setTabActivo('compras')}
        >
          📥 Compras
        </button>
      </div>

      {/* Tabs de categorías */}
      <div style={s.tabs}>
        {CATEGORIAS.map(c => (
          <button
            key={c.key}
            style={{ ...s.tab, ...(categoriaActiva === c.key ? { ...s.tabActivo, borderBottomColor: c.color } : {}) }}
            onClick={() => setCategoriaActiva(c.key)}
          >
            {c.label}
            {datos && (
              <span style={{ ...s.tabBadge, background: categoriaActiva === c.key ? c.color : '#2d2d44' }}>
                {datos[c.key]?.productos.length || 0}
              </span>
            )}
          </button>
        ))}
      </div>

      <div style={s.contenido}>
        {cargando ? (
          <p style={{ color: '#aaa', padding: 40, textAlign: 'center' }}>Cargando datos...</p>
        ) : !datos ? (
          <p style={{ color: '#e63946', padding: 40, textAlign: 'center' }}>No se pudo cargar</p>
        ) : (
          <TablaTop
            datos={datos[categoriaActiva]}
            color={categoriaActual.color}
            esCompra={tabActivo === 'compras'}
          />
        )}
      </div>
    </div>
  );
}

const s = {
  titulo: { color: '#fff', margin: 0 },
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 16 },
  filtrosRow: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' },
  botonRapido: { background: '#2d2d44', border: 'none', borderRadius: 6, color: '#aaa', padding: '8px 14px', cursor: 'pointer', fontSize: 13 },
  inputFecha: { background: '#1e1e2e', border: '1px solid #2d2d44', borderRadius: 6, color: '#fff', padding: '8px 12px', fontSize: 13 },
  botonAplicar: { background: '#e63946', border: 'none', borderRadius: 6, color: '#fff', padding: '8px 16px', cursor: 'pointer', fontWeight: 'bold', fontSize: 13 },
  periodoTxt: { color: '#888', fontSize: 12, marginBottom: 8 },
  tabsMain: { display: 'flex', gap: 4, marginBottom: 0, borderBottom: '2px solid #2d2d44' },
  tabMain: { background: 'none', border: 'none', borderBottom: '3px solid transparent', color: '#888', padding: '12px 24px', cursor: 'pointer', fontSize: 15, fontWeight: '500', marginBottom: '-2px' },
  tabMainActivo: { color: '#fff', borderBottomColor: '#4cc9f0', fontWeight: 'bold' },
  tabs: { display: 'flex', gap: 4, marginBottom: 0, borderBottom: '1px solid #2d2d44', marginTop: 4 },
  tab: { background: 'none', border: 'none', borderBottom: '2px solid transparent', color: '#888', padding: '10px 16px', cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 },
  tabActivo: { color: '#fff', fontWeight: 'bold' },
  tabBadge: { fontSize: 10, borderRadius: 10, padding: '2px 6px', color: '#fff' },
  contenido: { background: '#1e1e2e', borderRadius: '0 0 12px 12px', padding: 24, minHeight: 300 },
  marcaLider: { color: '#aaa', fontSize: 13, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  marcaTotal: { color: '#555', fontSize: 12 },
  tabla: { marginTop: 24 },
  tablaHeader: { display: 'flex', color: '#555', fontSize: 11, fontWeight: 'bold', padding: '8px 0', borderBottom: '1px solid #2d2d44', gap: 8 },
  tablaRow: { display: 'flex', padding: '12px 0', borderBottom: '1px solid #1a1a2e', gap: 8, alignItems: 'center' },
  itemNombre: { color: '#fff', fontSize: 13, marginBottom: 2 },
  itemCodigo: { color: '#555', fontSize: 11 },
  barraContainer: { background: '#2a2a3e', borderRadius: 3, height: 4, marginBottom: 4 },
  barraFill: { height: 4, borderRadius: 3 },
};